import { applyMove, applyRoll, chooseBotMove, createMatch, type BotDifficulty, type Color, type MatchState, type PlayerStatus, type SeatConfig } from "@ludo/engine";

export type LocalSeatType = Extract<PlayerStatus, "human" | "bot" | "empty">;

export interface OfflineSeatSetup {
  seat: Color;
  type: LocalSeatType;
  name: string;
  difficulty: BotDifficulty;
}

export type OfflineMatchSetup = readonly OfflineSeatSetup[];

const defaultNames = ["You", "Azure", "Jade", "Gold"];

export function defaultOfflineSetup(mode: "quick" | "practice" = "quick"): OfflineSeatSetup[] {
  return [
    { seat: 0, type: "human", name: "You", difficulty: "medium" },
    { seat: 1, type: mode === "practice" ? "bot" : "human", name: mode === "practice" ? "Azure" : "Player 2", difficulty: "medium" },
    { seat: 2, type: "empty", name: "Jade", difficulty: "easy" },
    { seat: 3, type: "empty", name: "Gold", difficulty: "hard" },
  ];
}

function toEngineSeat(config: OfflineSeatSetup): SeatConfig {
  const displayName = config.name.trim() || defaultNames[config.seat] || `Player ${config.seat + 1}`;
  if (config.type === "empty") return { seat: config.seat, userId: null, displayName, status: "empty" };
  if (config.type === "bot") return { seat: config.seat, userId: null, displayName, bot: config.difficulty, status: "bot" };
  return { seat: config.seat, userId: `local:${config.seat}`, displayName, status: "human" };
}

export function createOfflineMatch(setup: OfflineMatchSetup = defaultOfflineSetup(), now = Date.now()): MatchState {
  return createMatch(`offline:${now}`, "offline", setup.map(toEngineSeat), "offline", now, { autoMove: true });
}

export function offlineDie(): number {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return (value[0] ?? 0) % 6 + 1;
}

export function rollOffline(state: MatchState, now = Date.now()): MatchState {
  const d1 = offlineDie();
  const d2 = offlineDie();
  return applyRoll(state, [d1, d2], `offline:${state.rollNonce}`, now).state;
}

export function moveOffline(state: MatchState, tokenId: string, now = Date.now()): MatchState {
  return applyMove(state, tokenId, now).state;
}

export function playBotStep(state: MatchState, now = Date.now()): MatchState {
  if (state.phase === "roll") return rollOffline(state, now);
  if (state.phase === "move") {
    const move = chooseBotMove(state);
    return move ? moveOffline(state, move.tokenId, now) : state;
  }
  return state;
}
